import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";

const LINKS = [
  { id: "shop", label: "Shop" },
  { id: "spaces", label: "Spaces" },
  { id: "story", label: "Story" },
  { id: "craft", label: "Craft" },
  { id: "visit", label: "Visit" },
  { id: "ambaari", label: "Ambaari" },
];

const ROUTE_MAP = {
  top: "/",
  shop: "/shop",
  spaces: "/spaces",
  story: "/story",
  journey: "/story",
  founder: "/story",
  craft: "/craft",
  visit: "/visit",
  ambaari: "/ambaari",
  book: "/book",
};

export const scrollToId = (id) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(`#${id}`, { offset: 0, duration: 1.6 });
  } else {
    document.getElementById(id)?.scrollIntoView();
  }
};

export const useGo = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (id) => {
    const route = ROUTE_MAP[id] || "/";
    if (pathname === "/" && document.getElementById(id)) {
      scrollToId(id);
    } else if (route !== pathname) {
      navigate(route);
    } else {
      window.__lenis?.scrollTo(0, { duration: 1.2 });
    }
  };
};

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const go = (id) => navigate(ROUTE_MAP[id] || "/");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || pathname !== "/";

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid ? "bg-oxblood-ink/85 backdrop-blur-md border-b border-ivory/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-12 py-4">
        <button
          data-testid="nav-logo"
          onClick={() => go("top")}
          className="flex items-center group"
        >
          <img
            src="/logo-ivory.png"
            alt="Jaipur Furniture by Yojna Jaisingh"
            className="h-12 w-auto group-hover:opacity-80 transition-opacity duration-300"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => {
            const active = pathname === ROUTE_MAP[l.id];
            return (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`${
                  active ? "text-brass-soft" : "text-ivory/70"
                } hover:text-brass-soft text-xs tracking-[0.25em] uppercase transition-colors duration-300`}
              >
                {l.label}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="nav-book-btn"
            onClick={() => go("book")}
            className="bg-brass text-oxblood-ink hover:bg-brass-soft text-xs tracking-[0.22em] uppercase px-4 md:px-6 py-3 transition-colors duration-400"
          >
            Book Online
          </button>
          <button
            data-testid="nav-menu-btn"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Open menu"
            className="lg:hidden flex flex-col justify-center items-center gap-1.5 w-11 h-11 border border-ivory/30"
          >
            <span className={`block w-5 h-px bg-ivory transition-transform duration-300 ${menuOpen ? "rotate-45 translate-y-[3.5px]" : ""}`} />
            <span className={`block w-5 h-px bg-ivory transition-transform duration-300 ${menuOpen ? "-rotate-45 -translate-y-[3.5px]" : ""}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen ? (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-oxblood-ink/95 backdrop-blur-lg flex flex-col justify-center px-10 lg:hidden"
          >
            <nav className="flex flex-col gap-7">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-link-${l.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => { setMenuOpen(false); go(l.id); }}
                  className="text-left font-display text-ivory text-4xl hover:text-brass-soft transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-link-book"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + LINKS.length * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => { setMenuOpen(false); go("book"); }}
                className="text-left font-display italic text-brass-soft text-4xl"
              >
                Book Online →
              </motion.button>
            </nav>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.header>
  );
}
